import React from 'react';
import { styled, css } from '@compiled/react';
import Heading from '@atlaskit/heading';
import IssuesIcon from '@atlaskit/icon/glyph/issues';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import commonMessages from '../../../common/messages';
import { TrendSummary } from '../../../common/ui/trend-summary';
import { useFormatDurationFromSeconds } from '../../../common/utils/date-range';
import type { BarChartBarData } from '../../../services/historical-cycle-time/types';
import messages from './messages';

type Props = {
	hasPermissionForAllContributingIssues: boolean;
	selectedBarData: BarChartBarData;
	wholePeriodStatistics: number | null;
};

export const SelectionDetail = ({
	selectedBarData,
	wholePeriodStatistics,
	hasPermissionForAllContributingIssues,
}: Props) => {
	const { formatDate, formatMessage } = useIntl();
	const formatDurationFromSeconds = useFormatDurationFromSeconds();
	if (selectedBarData.cycleTimeStatistics === null) return null;

	const durationMessage = formatDurationFromSeconds(selectedBarData.cycleTimeStatistics, {
		days: 'long',
		hours: 'short',
	});
	return (
		<Wrapper>
			<Header>
				<Heading level="h300">
					<WeekStartText>
						{formatMessage(messages.weekStartDate, {
							date: formatDate(selectedBarData.startDateInclusive, {
								day: 'numeric',
								month: 'short',
							}),
						})}
					</WeekStartText>
				</Heading>
			</Header>
			<Title data-test-id="software-cycle-time-breakdown-report.ui.historical-cycle-time.selection-detail.title">
				{durationMessage}
				<MedianCycleTime>{formatMessage(commonMessages.medianCycleTime)}</MedianCycleTime>
			</Title>

			{hasPermissionForAllContributingIssues && (
				<IssuesShippedWrapper>
					<IssuesIcon size="small" label="" />
					<IssueShippedText>
						{formatMessage(messages.issueShipped, {
							numIssues: selectedBarData.numIssues,
						})}
					</IssueShippedText>
				</IssuesShippedWrapper>
			)}
			{wholePeriodStatistics !== null && selectedBarData.cycleTimeStatistics !== null && (
				<TrendSummary
					currentDuration={selectedBarData.cycleTimeStatistics}
					overallDuration={wholePeriodStatistics}
				/>
			)}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	height: '96px',
	padding: token('space.100', '8px'),
});

const headerBaseStyles = css({
	display: 'flex',
	height: token('space.200', '16px'),
	alignItems: 'center',
	marginTop: token('space.150', '12px'),
	marginBottom: token('space.075', '6px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Header = styled.div(headerBaseStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WeekStartText = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const MedianCycleTime = styled.p({
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssuesShippedWrapper = styled.div({
	display: 'flex',
	gap: token('space.050', '4px'),
	height: token('space.200', '16px'),
	alignItems: 'center',
	marginBottom: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const IssueShippedText = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N300),
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '20px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N500),
	marginBottom: token('space.050', '4px'),
});
